import React, { useState } from 'react';
import { Mail, Loader2, Check } from 'lucide-react';
import Modal from '../ui/Modal';
import { supabase, retryOperation } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';

interface EmailVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVerified: () => void;
}

export default function EmailVerificationModal({ isOpen, onClose, onVerified }: EmailVerificationModalProps) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Create unauth user first
      const { data: userId, error: userError } = await retryOperation(() =>
        supabase.rpc('handle_unauth_user', {
          user_email: email.toLowerCase()
        })
      );

      if (userError) throw userError;

      // Send magic link
      const { error: magicLinkError } = await supabase.auth.signInWithOtp({
        email: email.toLowerCase(),
        options: {
          emailRedirectTo: `${window.location.origin}/magic-link-callback`,
        }
      });

      if (magicLinkError) throw magicLinkError;

      // Submit to Netlify Forms
      const formData = new FormData();
      formData.append('form-name', 'newsletter');
      formData.append('email', email);

      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as any).toString()
      });

      if (!response.ok) {
        console.warn('Failed to submit newsletter form, but continuing');
      }
      
      setSuccess(true);
      setTimeout(() => {
        onVerified();
      }, 1500);
    } catch (err) {
      console.error('Error verifying email:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="">
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-primary-100 rounded-full">
            <Mail className="h-8 w-8 text-primary-500" />
          </div>
        </div>
        
        <h3 className="text-xl font-semibold mb-2">
          {success ? 'Thank you!' : 'Quick Email Verification'}
        </h3>
        
        {success ? (
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-2 text-green-600">
              <Check className="h-5 w-5" />
              <p>Magic link sent! Check your email.</p>
            </div>
            <p className="text-gray-600 text-sm">
              We've sent a magic link to your email. Click the link to sign in and access your free resources!
            </p>
          </div>
        ) : (
          <>
            <p className="text-gray-600 mb-6">
              Enter your email to access this free resource and receive updates about new gardening content.
            </p>

            <form onSubmit={handleSubmit}>
              <div className="relative mb-4">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  required
                />
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              </div>

              {error && (
                <p className="text-sm text-red-600 mb-4">{error}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-primary-500 text-white py-2 px-4 rounded-md hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  'Continue to Download'
                )}
              </button>
            </form>

            <p className="text-xs text-gray-500 mt-4">
              By continuing, you agree to receive occasional updates about new gardening resources.
            </p>
          </>
        )}
      </div>
    </Modal>
  );
}